import { Injectable, Inject, LOCALE_ID, NgZone } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, startWith } from 'rxjs';
import { environment } from "../../environments/environment";
import { AffiliateConfigService } from "./affiliate-config.service";
import { MAX_CHARS } from './telemetry-context';

/**
 * What the Places flow reports to form telemetry (DEV-7892). FormService
 * subscribes and sends it; this service cannot inject FormService (cycle).
 */
export type PlacesTelemetry =
	| {
		type:             'selected';
		formGroup:        UntypedFormGroup;
		placeId:          string;
		formattedAddress: string;
		components:       Record<string, string>;
		filledFields:     string[];
		emptyFields:      string[];
		suggestionsCount: number;
		placesMs:         number;
	}
	| { type: 'error'; stage: 'script' | 'suggestions' | 'details'; message: string };

declare global {
	interface Window {
		google: any;
	}
}

@Injectable({
	providedIn: 'root'
})
export class GooglePlacesService {

	private googlePlacesLoaded = false;
	private currentSessionToken: any = null;

	/** Places selections and failures for form telemetry (DEV-7892). */
	public readonly telemetry$ = new Subject<PlacesTelemetry>();

	/** Suggestions shown for the last query, and when the last pick started (DEV-7892). */
	private lastSuggestionsCount = 0;
	private selectionStartedAt = 0;
	private unsupportedReported = false;

	constructor(@Inject(LOCALE_ID) public locale: string, private ngZone: NgZone, private affiliateConfigService: AffiliateConfigService) {}

	/**
	 * Check if the browser supports Google Places (requires HTMLDialogElement, which
	 * the Places library uses internally). Older Safari, Firefox, and in-app browsers may lack support.
	 */
	private isPlacesSupported(): boolean {
		return typeof HTMLDialogElement !== 'undefined';
	}

	/**
	 * Initialize autocomplete for a form control
	 * @param formGroup The form group containing the address field
	 * @param addressControlName The name of the address control (default: 'address')
	 * @param countryCode Optional country code to restrict search
	 * @returns Observable of autocomplete suggestions
	 */
	public initializeAutocomplete(
		formGroup: UntypedFormGroup,
		addressControlName: string = 'address',
		countryCode?: string
	): Observable<any[]> {

		// Check if we should skip autocomplete (if most fields are already filled)
		if (this.shouldSkipAutocomplete(formGroup)) {
			return this.createEmptyObservable();
		}

		if (!this.isPlacesSupported()) {

			if( ! this.unsupportedReported ) {

				this.unsupportedReported = true;
				this.reportError( 'script', 'Places not supported by this browser' );
			}

			return this.createEmptyObservable();
		}

		// Get country code for restriction
		const finalCountryCode = countryCode || formGroup.get('country')?.value;

		if (!finalCountryCode) {
			return this.createEmptyObservable();
		}

		// Set up autocomplete suggestions with debouncing
		return formGroup.get(addressControlName)?.valueChanges.pipe(
			startWith(''),
			debounceTime(350),
			distinctUntilChanged(),
			switchMap(value => {
				if (!this.isValidInput(value)) {
					return this.createEmptyObservable();
				}
				return this.fetchAutocompleteSuggestions(value, finalCountryCode);
			})
		) || this.createEmptyObservable();
	}


	/**
	 * Fetch autocomplete suggestions using the new Places API
	 * @param input The user input text
	 * @param countryCode Optional country code to restrict search
	 * @returns Observable of autocomplete suggestions
	 */
	private fetchAutocompleteSuggestions(input: string, countryCode?: string): Observable<any[]> {

		// Check if API key is available
		if (!environment.googleSireloApiKey) {
			console.warn('Google Places API key not found in environment configuration.');
			return this.createEmptyObservable();
		}

		// Return empty array if input is too short or not a string
		if (!this.isValidInput(input)) {
			return this.createEmptyObservable();
		}

		return new Observable(observer => {
			// Ensure Google Places is loaded before proceeding
			this.waitForGooglePlaces().then(async () => {
				try {
					// Import the Places library
					const { AutocompleteSessionToken, AutocompleteSuggestion } = await this.importPlacesLibrary();

					// Create new session token if we don't have one
					if (!this.currentSessionToken) {
						this.currentSessionToken = new AutocompleteSessionToken();
					}

					// Build the request object according to the API documentation
					const request: any = {
						input: input,
						sessionToken: this.currentSessionToken,
						language: this.locale.split('-')[0]
					};

					// Add country restriction if provided, ensuring countryCode is only 2 characters
					if (countryCode) {
						request.includedRegionCodes = [countryCode.slice(0, 2)];
					}

					// Use the new AutocompleteSuggestion API
					const response = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
					const suggestions = response.suggestions || [];
					this.lastSuggestionsCount = suggestions.length;
					observer.next(suggestions);
					observer.complete();
				} catch (error: any) {
					this.handleApiError(error, observer, 'fetchAutocompleteSuggestions');
				}
			}).catch((error: any) => {
				this.handleApiError(error, observer, 'waitForGooglePlaces');
			});
		});
	}

	/**
	 * Handle place selection from autocomplete suggestion
	 * @param suggestion The selected autocomplete suggestion
	 * @param formGroup The form group to populate
	 */
	public handleSuggestionSelected(suggestion: any, formGroup: UntypedFormGroup): void {

		if (!suggestion.placePrediction) {
			console.warn('No place prediction found in selected suggestion');
			return;
		}

		// Get the place ID and fetch place details
		const placeId = suggestion.placePrediction.placeId;
		this.selectionStartedAt = performance.now();
		
		this.fetchPlaceDetails(placeId).then(place => {
			this.handlePlaceChanged(formGroup, place);
		}).catch(error => {
			console.error('Error fetching place details:', error);
			this.reportError( 'details', error?.message ?? String( error ) );
		});
	}

	/**
	 * Fetch place details using place ID
	 * @param placeId The place ID to fetch details for
	 * @returns Promise with place details
	 */
	private fetchPlaceDetails(placeId: string): Promise<any> {

		return new Promise(async (resolve, reject) => {
			try {
				await this.waitForGooglePlaces();
				
				// Import the Places library
				const { Place } = await this.importPlacesLibrary();

				// Use the new Place API with correct field names
				const place = new Place({
					id: placeId
				});

				// Fetch the correct fields according to the new API documentation
				await place.fetchFields({
					fields: ['addressComponents', 'formattedAddress']
				});

				// Log the place details for debugging. Carries the street address and
				// postcode, and Bugsnag records console output as a breadcrumb - not
				// safe in production or staging (DEV-7386 follow-up).
				if( ! environment.production && ! environment.staging ) {

					console.log('Place details API response:', {
						placeId: placeId,
						place: place,
						addressComponents: place.addressComponents,
						formattedAddress: place.formattedAddress
					});
				}

				resolve(place);
			} catch (error: any) {
				reject(new Error('Place details request failed: ' + error.message));
			}
		});
	}

	/**
	 * Load Google Places API script
	 */
	private loadGooglePlacesScript(): void {

		if (this.googlePlacesLoaded) {
			return;
		}

		if (!this.isPlacesSupported()) {
			this.googlePlacesLoaded = true;
			return;
		}

		// Create script element for the new Maps API
		const script = document.createElement('script');
		script.type = 'text/javascript';
		const googleApiKey = this.affiliateConfigService.isAffiliateMode ? environment.googleAffiliateApiKey : environment.googleSireloApiKey;
		script.src = `https://maps.googleapis.com/maps/api/js?key=${googleApiKey}&libraries=places&language=${this.locale.split('-')[0]}&loading=async`;
		script.async = true;
		script.defer = true;

		// Add script to head
		document.head.appendChild(script);

		// Mark as loaded
		this.googlePlacesLoaded = true;
	}

	/**
	 * Wait for Google Places API to be available
	 */
	private waitForGooglePlaces(): Promise<void> {

		return new Promise((resolve, reject) => {

			if (!this.isPlacesSupported()) {
				reject(new Error('Browser does not support Places Autocomplete'));
				return;
			}

			// Load script if not already loaded
			if (!this.googlePlacesLoaded) {
				this.loadGooglePlacesScript();
			}

			const checkGooglePlaces = () => {

				if (window.google && window.google.maps && window.google.maps.places) {
					resolve();
				} else {
					setTimeout(checkGooglePlaces, 100);
				}
			};

			checkGooglePlaces();
		});
	}

	/**
	 * Import the Places library and get the required classes
	 */
	private async importPlacesLibrary(): Promise<any> {

		try {
			const { Place, AutocompleteSessionToken, AutocompleteSuggestion } = await window.google.maps.importLibrary("places");
			return { Place, AutocompleteSessionToken, AutocompleteSuggestion };
		} catch (error) {
			console.error('Error importing Places library:', error);
			throw error;
		}
	}


	/**
	 * Handle place selection from autocomplete
	 */
	private handlePlaceChanged(
		formGroup: UntypedFormGroup,
		place: any
	): void {

		// Check if place has address components (new Place API structure)
		const addressComponents = place.addressComponents;

		// Same reason as above: carries the street address, so keep it out of
		// production and staging.
		if( ! environment.production && ! environment.staging ) {

			console.log('Processing place in handlePlaceChanged:', {
				place: place,
				addressComponents: addressComponents,
				formattedAddress: place.formattedAddress
			});
		}

		if( ! addressComponents ) {

			console.warn('No address components found in selected place');
			this.reportError( 'details', 'No address components in the selected place' );
			return;
		}

		this.ngZone.run( () => {

			// Prepare to collect values
			let street = '';
			let houseNumber = '';
			let postcode = '';
			let city = '';
			let country = '';
			let state = '';

			// Grab sublocality_level_1 as potential fallback for address
			let sublocalityLevel1 = '';

			// Grab administrative_area_level_2 as potential fallback for city
			let adminAreaLevel2 = '';

			addressComponents.forEach( ( component: any ) => {

				// Each component individually carries a street/postcode fragment.
				if( ! environment.production && ! environment.staging ) {

					console.log('Processing address component:', {
						component: component,
						types: component.types,
						shortText: component.shortText,
						longText: component.longText
					});
				}

				const types = component.types;
				let itemType = types[0];

				// If type 0 is colloquial_area and type 1 is locality
				if( itemType === 'colloquial_area' && types[1] === 'locality' ) {

					// Reset the type to locality
					itemType = 'locality';
				}

				// We normally select the short name (new API uses shortText)
				let itemValue = component.shortText;

				// If is city and shortname is less than 3 characters
				if( itemValue && itemValue.length < 3 && ( itemType === 'locality' || itemType === 'postal_town' ) ) {

					// Use the long name (new API uses longText)
					itemValue = component.longText;
				}

				switch( itemType ) {

					case 'route':
						street = itemValue;
						break;

					case 'street_number':
						houseNumber = itemValue;
						break;

					case 'postal_code':
						postcode = itemValue;
						break;

					case 'locality':
					case 'postal_town':
						city = itemValue;
						break;

					case 'country':
						country = itemValue;
						break;

					// New: capture sublocality_level_1
					case 'sublocality_level_1':
						sublocalityLevel1 = itemValue;
						break;

					// New: capture administrative_area_level_2
					case 'administrative_area_level_2':
						adminAreaLevel2 = itemValue;
						break;

					// Capture state/province from administrative_area_level_1
					case 'administrative_area_level_1':
						state = itemValue;
						break;
				}
			});

			// Merge street and houseNumber into address
			let address = '';
			if( street && houseNumber ) {

				address = `${street} ${houseNumber}`;
			}
			else if( street ) {

				address = street;
			}
			else if( houseNumber ) {

				address = houseNumber;
			}
			// Use sublocality_level_1 if no street/houseNumber
			else if( sublocalityLevel1 ) {

				address = sublocalityLevel1;
			}

			// Fallback for city: use administrative_area_level_2 if city is empty
			if( ! city && adminAreaLevel2 ) {

				city = adminAreaLevel2;
			}

			// Set form values after gathering all variables

			// Logged as a bare string, not an object - no key-based rule can ever
			// redact this one, so it must stay behind the guard rather than gain
			// a pattern.
			if( ! environment.production && ! environment.staging ) {

				console.log( 'setting address to ', address );
			}

			this.setFormValueAndMark( 'address', address, formGroup );
			this.setFormValueAndMark( 'postcode', postcode, formGroup );
			this.setFormValueAndMark( 'city', city, formGroup );

			// Apply state value if available and state field exists
			if (state && formGroup.get('state')) {
				this.setFormValueAndMark( 'state', state, formGroup );
			}

			// Log the place selection for debugging
			if( ! environment.production && ! environment.staging ) {

				console.log( 'Google Places selection:', {
					address: formGroup.get( 'address' )?.value,
					postcode: formGroup.get( 'postcode' )?.value,
					city: formGroup.get( 'city' )?.value,
					country: formGroup.get( 'country' )?.value,
					state: formGroup.get( 'state' )?.value,
					place
				} );
			}

			// Place selection completed successfully
			this.reportSelected( formGroup, place, { address, postcode, city, state } );

			// Reset session token after successful place selection
			this.currentSessionToken = null;
		});
	}

	/**
	 * Check if enough data is already filled to skip autocomplete initialization
	 */
	public shouldSkipAutocomplete(formGroup: UntypedFormGroup): boolean {

		const address = formGroup.get('address')?.value;
		const postcode = formGroup.get('postcode')?.value;
		const city = formGroup.get('city')?.value;
		const country = formGroup.get('country')?.value;

		// Skip if we have most of the required fields filled
		const hasAddress = address && address.length > 0;
		const hasPostcode = postcode && postcode.length > 0;
		const hasCity = city && city.length > 0;
		const hasCountry = country && country.length > 0;

		// If we have at least 3 out of 4 fields filled, skip autocomplete
		const filledFields = [hasAddress, hasPostcode, hasCity, hasCountry].filter(Boolean).length;
		return filledFields >= 3;
	}

	/**
	 * Clean up autocomplete instances
	 */
	public cleanup(): void {

		// Reset session token
		this.currentSessionToken = null;
	}

	// ============================================================================
	// HELPER METHODS
	// ============================================================================

	/**
	 * Creates an empty observable that emits an empty array and completes immediately.
	 * Used when API calls can't be made (missing API key, invalid input, etc.)
	 * but the method still needs to return a valid Observable for components to subscribe to.
	 */
	private createEmptyObservable(): Observable<any[]> {
		return new BehaviorSubject([]).asObservable();
	}

	/**
	 * Validates user input before making API calls
	 * @param value The input value to validate
	 * @returns true if input is valid (string with at least 2 characters)
	 */
	private isValidInput(value: any): boolean {
		return value && typeof value === 'string' && value.length >= 2;
	}

	/**
	 * Centralized error handling for API calls
	 * @param error The error that occurred
	 * @param observer The observable observer
	 * @param context Context string to identify where the error occurred
	 */
	private handleApiError(error: any, observer: any, context: string): void {
		console.error(`Error in ${context}:`, error);
		this.reportError( context === 'waitForGooglePlaces' ? 'script' : 'suggestions', error?.message ?? String( error ) );
		observer.next([]);
		observer.complete();
	}

	/**
	 * Report a pick to telemetry (DEV-7892): what Google returned, which
	 * fields it filled and which it left empty. FormService records the
	 * address source from it.
	 */
	private reportSelected( formGroup: UntypedFormGroup, place: any, fields: Record<string, string | undefined> ): void {

		try {

			const components: Record<string, string> = {};
			const filledFields: string[] = [];
			const emptyFields: string[] = [];

			for( const [ name, value ] of Object.entries( fields ) ) {

				if( name === 'state' && ! formGroup.get( 'state' ) ) {
					continue;
				}

				components[ name ] = ( value ?? '' ).slice( 0, MAX_CHARS );
				( value ? filledFields : emptyFields ).push( name );
			}

			this.telemetry$.next( {
				type:             'selected',
				formGroup,
				placeId:          String( place?.id ?? '' ).slice( 0, MAX_CHARS ),
				formattedAddress: String( place?.formattedAddress ?? '' ).slice( 0, MAX_CHARS ),
				components,
				filledFields,
				emptyFields,
				suggestionsCount: this.lastSuggestionsCount,
				placesMs:         Math.round( performance.now() - this.selectionStartedAt ),
			} );

		} catch {
			// Never surface telemetry errors to the form.
		}
	}

	private reportError( stage: 'script' | 'suggestions' | 'details', message: string ): void {

		try {
			this.telemetry$.next( { type: 'error', stage, message: String( message ).slice( 0, MAX_CHARS ) } );
		} catch {
			// Never surface telemetry errors to the form.
		}
	}

	/**
	 * Sets form control value and marks it as dirty/touched
	 * @param controlName The name of the form control
	 * @param value The value to set
	 * @param formGroup The form group containing the control
	 */
	private setFormValueAndMark( controlName: string, value: any, formGroup: UntypedFormGroup ): void {

		const control = formGroup.get( controlName );

		if( control ) {

			control.setValue( value );

			if( value ) {

				control.markAsDirty();
				control.markAsTouched();
			}
		}
	}
} 